import React, { useEffect, useState } from 'react';
import { MoveHandler, Tree } from 'react-arborist';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, styled, Typography } from '@mui/material';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { TBusinessFormData } from 'src/@types';
import FormProvider from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { FormInnerContainer, FormLabel } from 'src/layouts/form/FormLayout';
import { IBusiness, ICategory, useStores } from 'src/models';
import { businessApi, categoryApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FORM_IDS } from 'src/static/constants';

import convertMenusToTreeItems from '../utils/orderEdit/buildTreeData';
import findBusinessInTree from '../utils/orderEdit/findBusinessInTree';
import findCategoryInTree from '../utils/orderEdit/findCategoryInTree';

import { NodeItem } from './NodeItem';
import { ITreeObj } from './orderEditTypes';

export interface ICtegryOrderForm {
  ctegrySid: number;
  pctegrySid?: number | null;
  ctegryList?: ICtegryOrderForm[] | null;
}
export type MyData = {
  id: string;
  isOpen: boolean;
  name: string;
  children?: MyData[];
};

export const OrderEditDialog = observer(({ type }: { type: 'category' | 'business' }) => {
  const { t } = useTranslation();
  const { categoryStore, alertStore, loadingStore } = useStores();
  const handleResponse = useHandleResponse();
  const methods = useForm<ICategory>({});
  const [treeData, setTreeData] = useState<ITreeObj[]>([]);
  const [selectedLang, setSelectedLang] = useState(localStorage.getItem('i18nextLng') || 'ko');

  useEffect(() => {
    fetchCategoryTree();
  }, [selectedLang]);

  // 전체 카테고리 목록 조회
  const fetchCategoryTree = async () => {
    loadingStore.setLoading(true);
    const [fullTree, businessData] = await Promise.all([
      categoryStore.getCategoryTree(),
      type === 'business' ? businessApi.getBusinessOrderList(selectedLang ?? '') : null,
    ]);
    if (fullTree) {
      setTreeData(
        convertMenusToTreeItems(
          type,
          fullTree?.data as ICategory[],
          businessData?.data as IBusiness[],
          selectedLang,
        ),
      );
    }
    loadingStore.setLoading(false);
  };

  // onMove 핸들러: 이동된 노드를 기반으로 트리 데이터를 업데이트
  const handleMove: MoveHandler<ITreeObj> = async ({
    dragNodes, // 드래그된 노드 객체 배열
    parentId, // 드롭된 위치의 부모 노드 ID
    index, // 드롭된 위치의 인덱스
  }) => {
    // 드래그된 노드의 ID
    const draggedId = dragNodes[0].id;
    // 드래그된 노드가 비즈니스 노드인지 확인
    const isBusinessNode = draggedId.startsWith('business-');

    alertStore.setProps({
      open: true,
      title: '순서 변경 안내',
      content: isBusinessNode
        ? '비즈니스 순서 변경 시 즉시 반영되며, 파이프라인이 초기화될 수 있습니다.'
        : '카테고리 순서 변경 시 즉시 반영됩니다.',
      category: 'info',
      hasCancelButton: true,
    });

    alertStore.setOnCancel(async () => {
      await fetchCategoryTree();
    });

    alertStore.setOnConfirm(async () => {
      loadingStore.setLoading(true);
      try {
        if (isBusinessNode) {
          // 비즈니스 노드 처리
          const businessSid = parseInt(draggedId.replace('business-', ''));
          const businessData = await businessApi.getBusinessOrderList(selectedLang ?? '');
          const movedBusiness = findBusinessInTree(businessData?.data as IBusiness[], businessSid);

          if (movedBusiness) {
            await handleBusinessMove(movedBusiness, parentId, index);
          }
        } else {
          // 카테고리 노드 처리
          await handleCategoryMove(dragNodes[0].id, parentId, index);
        }

        // 공통: 트리 새로고침
        await fetchCategoryTree();
        loadingStore.setLoading(false);
      } catch (error) {
        console.error('Failed to update order:', error);
        loadingStore.setLoading(false);
      }
    });
  };

  // 비즈니스 이동 처리 함수
  const handleBusinessMove = async (
    business: IBusiness,
    newCategoryId: string | null,
    newIndex: number,
  ) => {
    // get으로 받아온 데이터를 바로 수정하기 위해 썸네일 제외
    const { businessSid, thumbnail, ...rest } = business;
    const updatedBusiness = {
      ...rest,
      categorySid: newCategoryId ? parseInt(newCategoryId) : null,
      ordr: newIndex + 1,
    };

    const result = await businessApi.put(businessSid ?? 0, updatedBusiness as TBusinessFormData);
    if (result) {
      handleResponse('순서가 변경되었습니다.', '순서 변경에 실패했습니다.');
    }
  };

  // 카테고리 이동 처리 함수
  const handleCategoryMove = async (categoryId: string, parentId: string | null, index: number) => {
    const movedCategory = findCategoryInTree(
      toJS(categoryStore.categoryTree),
      parseInt(categoryId),
    );

    if (!movedCategory) return;

    const { desktopImageFile, mobileImageFile, ...restCategory } = movedCategory;
    const updatedCategory = {
      ...restCategory,
      ordr: index + 1,
      categoryPsid: parentId ? parseInt(parentId) : null,
      ctgyDpTypeCd: movedCategory.ctgyDpTypeCd?.code,
    };

    const result = await categoryApi.putOrder(movedCategory.categorySid, updatedCategory);
    if (result) {
      handleResponse('순서가 변경되었습니다.', '순서 변경에 실패했습니다.');
    }
  };

  return (
    <FormProvider
      methods={methods}
      formId={FORM_IDS.CATEGORY_ORDER}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 10,
      }}
    >
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          <Iconify icon="mdi:drag-indicator" sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('category.orderChange.description')}
        </Typography>
      </Box>
      {type === 'business' && (
        <LanguageButtonContainer>
          <LanguageButton
            variant={selectedLang === 'ko' ? 'contained' : 'outlined'}
            onClick={() => setSelectedLang('ko')}
            isSelected={selectedLang === 'ko'}
          >
            한글
          </LanguageButton>
          <LanguageButton
            variant={selectedLang === 'en' ? 'contained' : 'outlined'}
            onClick={() => setSelectedLang('en')}
            isSelected={selectedLang === 'en'}
          >
            영어
          </LanguageButton>
        </LanguageButtonContainer>
      )}
      <StyledFormContainer>
        <FormLabel>
          {t('category.orderChange.title')}
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            {t('category.orderChange.subDescription')}
          </Typography>
        </FormLabel>
        <Tree
          className="react-aborist"
          data={treeData}
          onMove={handleMove}
          openByDefault={true}
          indent={15}
          width="100%"
          rowHeight={type === 'business' ? 56 : 40}
        >
          {NodeItem}
        </Tree>
      </StyledFormContainer>
    </FormProvider>
  );
});

const StyledFormContainer = styled(FormInnerContainer)(({ theme }) => ({
  minHeight: 352,
  minWidth: 250,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '& .react-arborist': {
    '& .node-container': {
      transition: 'background-color 0.2s',
      cursor: 'grab',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.is-dragging': {
        backgroundColor: theme.palette.action.selected,
        opacity: 0.7,
      },
    },
    '& .drop-target': {
      borderTop: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

const LanguageButtonContainer = styled(Box)({
  display: 'flex',
  gap: 1,
  justifyContent: 'flex-end',
});

const LanguageButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  minWidth: 60,
  backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.background.paper,
  },
}));
